import { useCallback, useEffect, useState } from 'react';

import { getPinnedConversationLimitErrorData } from '@atlassian/conversation-assistant-service-api/utils/api-error';

import type { Conversation } from '../types';

const PINNED_CONVERSATION_LIMIT_VALUE_PATTERN = /\b(\d+)\b/g;
const PIN_LIMIT_POPUP_AUTO_DISMISS_MS = 5000;

export const getPinnedConversationLimitError = (
	error: unknown,
): { maxPinnedConversations?: number } | undefined => {
	const structuredData = getPinnedConversationLimitErrorData(error);
	if (structuredData === undefined) {
		return undefined;
	}
	if (structuredData.maxPinnedConversations !== undefined) {
		return { maxPinnedConversations: structuredData.maxPinnedConversations };
	}

	// Backward compatibility for older backend versions that return the stable code without
	// structured data. Detection never depends on this human-readable message.
	const errorAttributes =
		typeof error === 'object' && error !== null && 'errorAttributes' in error
			? (error.errorAttributes as { rawErrorMessage?: string })
			: undefined;
	const matches = (errorAttributes?.rawErrorMessage ?? '').match(
		PINNED_CONVERSATION_LIMIT_VALUE_PATTERN,
	);
	const limitValue = matches?.[matches.length - 1];
	const limit = limitValue !== undefined ? Number(limitValue) : Number.NaN;

	return Number.isFinite(limit) ? { maxPinnedConversations: limit } : {};
};

type PinLimitError = {
	conversationId: string;
	maxPinnedConversations?: number;
} | null;

type OptimisticPinnedState = {
	isPinned: boolean;
	pinnedAt?: string | null;
	/**
	 * The server-side `isPinned` value at the moment this optimistic toggle was recorded. The
	 * optimistic value only applies while the server still reports this; once the server changes
	 * (catches up, or diverges), the entry is stale and we fall back to the server value. This lets
	 * us reconcile during render instead of syncing state in an effect.
	 */
	basedOnServerPinned: boolean;
};

/**
 * Resolves the effective pinned state for a conversation, preferring an in-flight optimistic toggle
 * while it is still based on the current server value, otherwise using the server value.
 */
const resolveIsPinned = (
	conversation: Conversation,
	optimisticState: OptimisticPinnedState | undefined,
): boolean => {
	const serverPinned = conversation.isPinned === true;
	if (optimisticState && optimisticState.basedOnServerPinned === serverPinned) {
		return optimisticState.isPinned;
	}
	return serverPinned;
};

type UsePinnedConversationsArgs = {
	conversations: Conversation[];
	isPinnedChatsEnabled: boolean;
	onConversationPinToggle?: (conversation: Conversation, isPinned: boolean) => void | Promise<void>;
	onBeforeOptimisticStateChange?: () => void;
	getConversationForPinToggle?: (conversation: Conversation) => Conversation;
};

type UsePinnedConversationsResult = {
	applyOptimisticPinnedState: (conversation: Conversation) => Conversation;
	compareConversationsByPinnedState: (a: Conversation, b: Conversation) => number;
	handleConversationPinToggle: (conversation: Conversation, isPinned: boolean) => boolean;
	pinLimitError: PinLimitError;
	dismissPinLimitError: (conversationId: string) => void;
};

const getConversationTimestamp = (timestamp?: string | null) => {
	if (!timestamp) {
		return undefined;
	}

	const time = new Date(timestamp).getTime();

	return Number.isNaN(time) ? undefined : time;
};

const compareConversationsByPinnedAt = (a: Conversation, b: Conversation) => {
	const aPinnedAt = getConversationTimestamp(a.pinnedAt);
	const bPinnedAt = getConversationTimestamp(b.pinnedAt);

	if (aPinnedAt === undefined && bPinnedAt === undefined) {
		return 0;
	}
	if (aPinnedAt === undefined) {
		return 1;
	}
	if (bPinnedAt === undefined) {
		return -1;
	}

	return bPinnedAt - aPinnedAt;
};

const compareConversationsByPinnedState = (a: Conversation, b: Conversation) => {
	if (a.isPinned === b.isPinned) {
		if (a.isPinned && b.isPinned) {
			return compareConversationsByPinnedAt(a, b);
		}

		return 0;
	}

	return a.isPinned ? -1 : 1;
};

const getOptimisticPinnedAt = (conversations: Conversation[]) => {
	const newestKnownPinnedAt = conversations.reduce((newestPinnedAt, conversation) => {
		const pinnedAt = getConversationTimestamp(conversation.pinnedAt);

		if (pinnedAt === undefined) {
			return newestPinnedAt;
		}

		return Math.max(newestPinnedAt, pinnedAt);
	}, 0);

	return new Date(Math.max(Date.now(), newestKnownPinnedAt + 1)).toISOString();
};

export const usePinnedConversations = ({
	conversations,
	isPinnedChatsEnabled,
	onConversationPinToggle,
	onBeforeOptimisticStateChange,
	getConversationForPinToggle,
}: UsePinnedConversationsArgs): UsePinnedConversationsResult => {
	const [optimisticPinnedByConversationId, setOptimisticPinnedByConversationId] = useState<
		Record<string, OptimisticPinnedState>
	>({});
	const [pinLimitError, setPinLimitError] = useState<PinLimitError>(null);

	const isConversationPinned = useCallback(
		(conversation: Conversation) =>
			isPinnedChatsEnabled &&
			resolveIsPinned(conversation, optimisticPinnedByConversationId[conversation.id]),
		[isPinnedChatsEnabled, optimisticPinnedByConversationId],
	);

	const handleConversationPinToggle = useCallback(
		(conversation: Conversation, isPinned: boolean) => {
			if (!isPinnedChatsEnabled) {
				return false;
			}

			// The `conversation` from a rendered row may already reflect an optimistic value; resolve
			// the server-truth entry so the optimistic state is based on the real server value.
			const serverConversation =
				conversations.find((candidate) => candidate.id === conversation.id) ?? conversation;
			const serverPinned = serverConversation.isPinned === true;
			setPinLimitError(null);
			onBeforeOptimisticStateChange?.();
			setOptimisticPinnedByConversationId((prev) => ({
				...prev,
				[conversation.id]: {
					isPinned,
					pinnedAt: isPinned ? getOptimisticPinnedAt(conversations) : null,
					basedOnServerPinned: serverPinned,
				},
			}));

			// Fire-and-forget: the optimistic update above already reflects the toggle, so we don't
			// block the event handler on the request; the catch reverts it if the request fails.
			void (async () => {
				try {
					await onConversationPinToggle?.(
						getConversationForPinToggle?.(conversation) ?? conversation,
						isPinned,
					);
				} catch (error) {
					const pinLimitError = isPinned ? getPinnedConversationLimitError(error) : undefined;

					// Roll back by dropping the optimistic entry: the toggle failed so the server value
					// is unchanged, and resolveIsPinned falls back to it.
					onBeforeOptimisticStateChange?.();
					setOptimisticPinnedByConversationId((prev) => {
						if (!Object.prototype.hasOwnProperty.call(prev, conversation.id)) {
							return prev;
						}
						const next = { ...prev };
						delete next[conversation.id];
						return next;
					});

					if (pinLimitError !== undefined) {
						setPinLimitError({
							conversationId: conversation.id,
							maxPinnedConversations: pinLimitError.maxPinnedConversations,
						});
					}
				}
			})();

			return true;
		},
		[
			isPinnedChatsEnabled,
			conversations,
			getConversationForPinToggle,
			onBeforeOptimisticStateChange,
			onConversationPinToggle,
		],
	);

	useEffect(() => {
		if (!pinLimitError) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setPinLimitError((current) =>
				current?.conversationId === pinLimitError.conversationId ? null : current,
			);
		}, PIN_LIMIT_POPUP_AUTO_DISMISS_MS);

		return () => clearTimeout(timeoutId);
	}, [pinLimitError]);

	// Note: optimistic entries are reconciled during render via `resolveIsPinned` (an entry is
	// ignored once the server value diverges from what it was based on), so no effect is needed to
	// prune them, and nothing needs to run when the feature is disabled.

	const applyOptimisticPinnedState = useCallback(
		(conversation: Conversation): Conversation => {
			const optimisticPinnedState = optimisticPinnedByConversationId[conversation.id];
			const isPinned = isConversationPinned(conversation);
			if (isPinned === (conversation.isPinned === true)) {
				return conversation;
			}

			return {
				...conversation,
				isPinned,
				pinnedAt: optimisticPinnedState?.pinnedAt ?? conversation.pinnedAt,
			};
		},
		[isConversationPinned, optimisticPinnedByConversationId],
	);

	const dismissPinLimitError = useCallback((conversationId: string) => {
		setPinLimitError((current) => (current?.conversationId === conversationId ? null : current));
	}, []);

	return {
		applyOptimisticPinnedState,
		compareConversationsByPinnedState,
		handleConversationPinToggle,
		pinLimitError,
		dismissPinLimitError,
	};
};
